import React, { use, useCallback, useMemo } from "react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import HStack from "../../ui_atoms/HStack";
import Switch from "../../ui_atoms/Switch";
import { useI18n, useSearchCountries } from "../../hooks/i18n-hooks";
import { MaritalStatus, WorkingPermitType } from "../../__generated__/graphql";
import { useMutation } from "@apollo/client/react";
import Loading from "../../components/Loading";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useTheme } from "../../hooks/use-theme";
import TextField from "../../components/TextField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SelectField from "../../components/SelectField";
import { isValidAVSNumber } from "../../avs";
import OptionsField from "../../components/OptionsField";
import { BodyMediumText, BodySmallText } from "../../components/FormattedText";
import ScreenCard from "../ScreenCard";
import { avsMask, cantonsMapping, fromMaybe } from "../../utils";
import { StyleSheet } from "react-native";
import { useCloseEditor } from "./use-close-editor";
import { useIntercom } from "../../hooks/intercom";
import MoneyField from "../../components/MoneyField";
import QuantityField from "../../components/QuantityField";
import InfoButton from "../../components/InfoButton";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import { hasPartner } from "../../hooks/use-get-is-worker-profile-complete";
import { CivilStateFormData, toCivilStateChanges } from "./worker-profile-changes";

const CANTONS = Object.entries(cantonsMapping)
    .map(([code, name]) => ({ value: code, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

type CivilStateEditorScreenProps = StackScreenProps<RootStackParamList, "CivilStateEdit">;

const CivilStateEditorScreen = ({ navigation, route }: CivilStateEditorScreenProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { profileId } = route.params || {};
    const { user, refreshUser } = use(AuthManagerContext);
    const { presentArticleId } = useIntercom();

    const [
        updateWorkerProfile,
        { loading: updateLoading, error: updateError, reset: updateReset },
    ] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    useApolloErrorSnackbar(
        updateError,
        updateReset,
        i18n.t("screens.profile_editor.civil_state.errors.update_worker_profile_error"),
    );

    const initialMaritalStatus = useMemo(
        () => user?.workerProfile?.maritalStatus,
        [user?.workerProfile?.maritalStatus],
    );

    const initialHasOtherActivity = useMemo(
        () => fromMaybe(user?.workerProfile?.hasOtherActivity),
        [user?.workerProfile?.hasOtherActivity],
    );

    const initialOtherActivityPercent = useMemo(
        () => fromMaybe(user?.workerProfile?.otherActivityPercent),
        [user?.workerProfile?.otherActivityPercent],
    );

    const workPermit = user?.workerProfile?.lastWorkPermit;
    const nationality = user?.nationality;
    const { paysWithholdingTax, livesAbroad } = usePostOnboardingData();
    const searchCountries = useSearchCountries();

    const initialChurchMember = fromMaybe(user?.workerProfile?.churchMember);
    const initialWeeklyResidenceCanton = fromMaybe(user?.workerProfile?.weeklyResidenceCanton);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        trigger,
        getValues,
        formState: { isValid, isDirty },
    } = useForm<CivilStateFormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            avsNumber: user?.workerProfile?.avsNumber || "",
            hasNoAvsNumber: false,
            maritalStatus: initialMaritalStatus || undefined,
            churchMember: initialChurchMember,
            // A saved church answer means the weekly residence question was answered with it: no
            // canton then means the worker goes home every night.
            sleepsInSwitzerland: initialWeeklyResidenceCanton
                ? true
                : initialChurchMember !== undefined
                  ? false
                  : undefined,
            weeklyResidenceCanton: initialWeeklyResidenceCanton,
            partner: {
                firstName: user?.workerProfile?.partner?.firstName || "",
                lastName: user?.workerProfile?.partner?.lastName || "",
                avsNumber: user?.workerProfile?.partner?.avsNumber || "",
                nationality: user?.workerProfile?.partner?.nationality || undefined,
                permitType: user?.workerProfile?.partner?.permitType || undefined,
                ...(user?.workerProfile?.partner?.monthlySalary === 0
                    ? {
                          hasRevenue: false,
                      }
                    : user?.workerProfile?.partner?.monthlySalary === null
                      ? {
                            hasRevenue: true,
                        }
                      : user?.workerProfile?.partner?.monthlySalary
                        ? {
                              hasRevenue: true,
                              monthlySalary: user.workerProfile.partner.monthlySalary / 100,
                          }
                        : {}),
            },
            ...(initialHasOtherActivity
                ? {
                      hasOtherActivity: true,
                      otherActivityPercent: initialOtherActivityPercent,
                  }
                : {
                      hasOtherActivity: false,
                  }),
        },
    });

    const maritalStatus = watch("maritalStatus");
    const partnerHasRevenue = watch("partner.hasRevenue");
    const avsNumber = watch("avsNumber");
    const partnerAvs = watch("partner.avsNumber");
    const hasOtherActivity = watch("hasOtherActivity");
    const hasNoAvsNumber = watch("hasNoAvsNumber");
    const partnerNationality = watch("partner.nationality");
    const sleepsInSwitzerland = watch("sleepsInSwitzerland");

    const isAvsMandatory = useMemo(
        () => nationality === "CH" || workPermit?.type === WorkingPermitType.PermitC,
        [nationality, workPermit],
    );

    const requirePartnerData = paysWithholdingTax && hasPartner(maritalStatus);

    // A worker living with a Swiss partner, or with a partner holding a permit C, pays no
    // withholding tax: asked to workers living in Switzerland only.
    const askPartnerNationality = requirePartnerData && !livesAbroad;
    const askPartnerPermit =
        askPartnerNationality && !!partnerNationality && partnerNationality !== "CH";

    const askOtherActivity = useMemo(
        () =>
            workPermit?.type &&
            [
                WorkingPermitType.PermitB,
                WorkingPermitType.PermitL,
                WorkingPermitType.PermitS,
            ].includes(workPermit?.type),
        [workPermit],
    );

    const onSubmit = useCallback<SubmitHandler<CivilStateFormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: toCivilStateChanges(data, { paysWithholdingTax, livesAbroad }),
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by updateError in useMutation
            }
        },
        [livesAbroad, navigation, paysWithholdingTax, refreshUser, updateWorkerProfile],
    );

    useCloseEditor(isDirty && !updateLoading);

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: updateLoading,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <VStack h="2/3" gap={1}>
                <Controller
                    control={control}
                    name="avsNumber"
                    rules={{
                        // Requiredness is enforced inside `validate` (not the `required` rule)
                        // because react-hook-form freezes a Controller's rules at mount, so a
                        // dynamic `required` would ignore the "no AVS number" toggle. `getValues`
                        // reads the live toggle value regardless of the captured closure.
                        validate: (v) => {
                            // AVS is not required for foreigners without a known permit,
                            // nor when the worker declares having no AVS number.
                            if ((!isAvsMandatory || getValues("hasNoAvsNumber")) && !v) {
                                // allow empty field
                                return true;
                            }
                            if (isValidAVSNumber(v)) {
                                if (!requirePartnerData || v !== partnerAvs) {
                                    return true;
                                } else {
                                    return i18n.t(
                                        "screens.profile_editor.civil_state.errors.not_unique_own_avs",
                                    );
                                }
                            } else {
                                return i18n.t("errors.avs_number");
                            }
                        },
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <TextField
                            error={error?.message}
                            value={value}
                            disabled={hasNoAvsNumber}
                            label={`${i18n.t("profile.avs_number")}${!isAvsMandatory ? ` (${i18n.t("optional").toLowerCase()})` : ``}`}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="numeric"
                            mask={avsMask}
                            maskAutoComplete
                            right={
                                <InfoButton
                                    onPress={() => {
                                        void presentArticleId("11177999");
                                    }}
                                />
                            }
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="hasNoAvsNumber"
                    render={({ field: { onChange, value } }) => (
                        <HStack>
                            <Switch
                                value={!!value}
                                onChange={(checked) => {
                                    onChange(checked);
                                    if (checked) {
                                        // Clear the AVS field when the worker has no AVS number
                                        setValue("avsNumber", "", { shouldDirty: true });
                                    }
                                    // Re-run AVS validation now that the toggle changed
                                    void trigger("avsNumber");
                                }}
                            />
                            <BodyMediumText style={styles.noAvsLabel}>
                                {i18n.t("screens.profile_editor.civil_state.no_avs_number")}
                            </BodyMediumText>
                        </HStack>
                    )}
                />
                <>
                    <Controller
                        control={control}
                        name="maritalStatus"
                        rules={{
                            required: true,
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <SelectField<string>
                                error={error?.message}
                                value={value}
                                style={{
                                    ...(requirePartnerData
                                        ? {
                                              borderBottomRightRadius: 0,
                                              borderBottomLeftRadius: 0,
                                          }
                                        : {}),
                                }}
                                data={Object.values(MaritalStatus).map((v) => ({
                                    value: v,
                                    label: i18n.t(`user.${v}`),
                                }))}
                                label={i18n.t("screens.profile_editor.civil_state.civil_state")}
                                onSelect={(e) => {
                                    onChange(e.value);
                                }}
                                onBlur={onBlur}
                            />
                        )}
                    />
                    {requirePartnerData && (
                        <>
                            <Controller
                                control={control}
                                name="partner.firstName"
                                rules={{
                                    required: true,
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value || ""}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_first_name",
                                            )}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    );
                                }}
                            />
                            <Controller
                                control={control}
                                name="partner.lastName"
                                rules={{
                                    required: true,
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={styles.stackedField}
                                            error={error?.message}
                                            value={value || ""}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_last_name",
                                            )}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                        />
                                    );
                                }}
                            />
                            <Controller
                                control={control}
                                name="partner.avsNumber"
                                rules={{
                                    required: false, // Partner's AVS is never required (could live abroad, for example)
                                    validate: (v) => {
                                        if (!v) {
                                            // allow empty field
                                            return true;
                                        } else if (isValidAVSNumber(v)) {
                                            if (v !== avsNumber) {
                                                return true;
                                            } else {
                                                return i18n.t(
                                                    "screens.profile_editor.civil_state.errors.not_unique_partner_avs",
                                                );
                                            }
                                        } else {
                                            return i18n.t("errors.avs_number");
                                        }
                                    },
                                }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => {
                                    return (
                                        <TextField
                                            isSelfContained
                                            style={
                                                askPartnerNationality
                                                    ? styles.stackedField
                                                    : styles.lastStackedField
                                            }
                                            error={error?.message}
                                            value={value || ""}
                                            label={`${i18n.t("screens.profile_editor.civil_state.partner_avs")} (${i18n.t("optional").toLowerCase()})`}
                                            onChangeText={onChange}
                                            onBlur={onBlur}
                                            keyboardType="numeric"
                                            mask={avsMask}
                                            maskAutoComplete
                                        />
                                    );
                                }}
                            />
                            {askPartnerNationality && (
                                <Controller
                                    control={control}
                                    name="partner.nationality"
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <SelectField<string>
                                            isSelfContained
                                            style={
                                                askPartnerPermit
                                                    ? styles.stackedField
                                                    : styles.lastStackedField
                                            }
                                            error={error?.message}
                                            value={value || undefined}
                                            data={searchCountries}
                                            searchPlaceholder={i18n.t(
                                                "screens.profile_editor.civil_state.search_country",
                                            )}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_nationality",
                                            )}
                                            onSelect={(e) => {
                                                onChange(e.value);
                                            }}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                            )}
                            {askPartnerPermit && (
                                <Controller
                                    control={control}
                                    name="partner.permitType"
                                    rules={{ required: true }}
                                    render={({
                                        field: { onChange, onBlur, value },
                                        fieldState: { error },
                                    }) => (
                                        <SelectField<WorkingPermitType>
                                            isSelfContained
                                            style={styles.lastStackedField}
                                            error={error?.message}
                                            value={value || undefined}
                                            data={Object.values(WorkingPermitType).map((v) => ({
                                                value: v,
                                                label: i18n.t(`work_permit.${v}`),
                                            }))}
                                            label={i18n.t(
                                                "screens.profile_editor.civil_state.partner_permit",
                                            )}
                                            onSelect={(e) => {
                                                onChange(e.value);
                                            }}
                                            onBlur={onBlur}
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}
                </>
                {requirePartnerData && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.partner_has_revenue")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="partner.hasRevenue"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
                {requirePartnerData && partnerHasRevenue && (
                    <VStack gap={0.5}>
                        <Controller
                            control={control}
                            name="partner.monthlySalary"
                            rules={{
                                required: false,
                                validate: (v) => {
                                    if (v === 0) {
                                        return i18n.t(
                                            "screens.profile_editor.civil_state.errors.partner_revenue_cannot_be_0",
                                        );
                                    }
                                    return true;
                                },
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <MoneyField
                                    error={error?.message}
                                    textAlign="right"
                                    value={value}
                                    label={i18n.t(
                                        "screens.profile_editor.civil_state.partner_revenue",
                                    )}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    selectTextOnFocus
                                    maxValue={99999}
                                    keyboardType="decimal-pad"
                                />
                            )}
                        />
                        <BodySmallText color={colors.fieldLabel}>
                            {i18n.t(
                                "screens.profile_editor.civil_state.partner_revenue_description",
                            )}
                        </BodySmallText>
                    </VStack>
                )}
                {paysWithholdingTax && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.church_member")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="churchMember"
                            rules={{
                                // A missing answer is taxed as a member, the tax office's default.
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
                {livesAbroad && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.sleeps_in_switzerland")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="sleepsInSwitzerland"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
                {livesAbroad && sleepsInSwitzerland && (
                    <Controller
                        control={control}
                        name="weeklyResidenceCanton"
                        rules={{ required: true }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <SelectField<string>
                                error={error?.message}
                                value={value}
                                data={CANTONS}
                                label={i18n.t(
                                    "screens.profile_editor.civil_state.weekly_residence_canton",
                                )}
                                onSelect={(e) => {
                                    onChange(e.value);
                                }}
                                onBlur={onBlur}
                            />
                        )}
                    />
                )}
                {askOtherActivity && (
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.has_other_activity")}
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="hasOtherActivity"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                )}
                {hasOtherActivity && (
                    <VStack gap={0.25}>
                        <Controller
                            control={control}
                            shouldUnregister
                            name="otherActivityPercent"
                            rules={{ required: false }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <QuantityField
                                    label={i18n.t(
                                        "screens.profile_editor.civil_state.work_rate_percentage",
                                    )}
                                    maxValue={100}
                                    right="%"
                                    value={value}
                                    onChange={onChange}
                                    keyboardType="decimal-pad"
                                    onBlur={onBlur}
                                    error={error?.message}
                                    returnKeyType="done"
                                />
                            )}
                        />
                        <BodySmallText color={colors.fieldLabel}>
                            {i18n.t(
                                "screens.profile_editor.civil_state.work_rate_percentage_description",
                            )}
                        </BodySmallText>
                    </VStack>
                )}
            </VStack>
        </ScreenCard>
    );
};

const styles = StyleSheet.create({
    stackedField: {
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        marginTop: -2,
    },
    lastStackedField: {
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        marginTop: -2,
    },
    noAvsLabel: {
        marginLeft: 12,
    },
});

export default CivilStateEditorScreen;
